// DevHub: Inline search modal — opens as overlay, results below input
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

// Kind filter options that map to search `types`. 'all' clears types (searches everything).
type KindFilter = 'all' | 'docs' | 'catalog';
import {
  SearchContextProvider,
  useSearch,
} from '@backstage/plugin-search-react';
import {
  appThemeApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { isdpAdminPagePermission, } from '@internal/plugin-isdp-common';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { noBrowserAutofill } from '../noBrowserAutofill';
import { useSearchStatus } from './useSearchStatus';
import { useDebouncedValue } from './useDebouncedValue';
import { useCurrentDoc, type CurrentDoc } from './useCurrentDoc';
// Extracted siblings: query logic, keyboard-nav, and the result-grouping region.
import { useZeroResultSuggestions } from './useZeroResultSuggestions';
import { useResultKeyboardNav } from './useResultKeyboardNav';
import { SearchModalResults } from './SearchModalResults';
import {
  buildCommands,
  matchCommands,
  type Command,
  type CommandContext,
} from './commandRegistry';
import { useRecentItems } from './useRecentItems';
import { searchMessages } from '../../i18n/search';
import './SearchModal.css';

// Delay before focusing the input on mount (lets the overlay paint first).
const FOCUS_DELAY_MS = 50;
// Delay before closing on result click, so the link navigation fires first.
const CLOSE_DELAY_MS = 100;

// Build the search filters that scope results to a single doc's TechDocs pages.
// The techdocs collator indexes name/kind/namespace for every page it emits.
function docFilters(doc: CurrentDoc) {
  return { name: doc.name, kind: doc.kind, namespace: doc.namespace };
}

function SearchModalContent({
  onClose,
  currentDoc,
}: {
  onClose: () => void;
  currentDoc: CurrentDoc | null;
}) {
  const { term, setTerm, setTypes, setFilters } = useSearch();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslationRef(searchMessages);

  // The input is a plain field styled exactly like the home hero (not MUI SearchBar). We keep a local
  // value that updates on every keystroke (so the field is instant, no lag) but DEBOUNCE committing it
  // to the shared search context `term`. Only `term` drives the backend query (via SearchContextProvider)
  // AND the /api/ai/search-suggest fetch below, so rapid typing collapses to ONE trailing request — the
  // fix for a dev spamming letters into the box and hammering the backend.
  const [inputValue, setInputValue] = useState(term);
  const debouncedInput = useDebouncedValue(inputValue, 250);
  useEffect(() => {
    if (debouncedInput !== term) setTerm(debouncedInput);
  }, [debouncedInput, term, setTerm]);
  // Reflect external term changes (e.g. a "Did you mean" chip calls setTerm) back into the field.
  useEffect(() => setInputValue(term), [term]);
  // Dynamic-key translator — commands carry i18n keys resolved at render.
  const tk = t as unknown as (k: string) => string;

  // ── Command palette layer ──────────────────────────────────────────────────
  // The palette shows navigable route commands + safe actions ABOVE live search.
  const appThemeApi = useApi(appThemeApiRef);
  const { allowed: isAdmin } = usePermission({ permission: isdpAdminPagePermission });
  const { recent, pushRecent } = useRecentItems();
  const [isDark, setIsDark] = useState(
    () => appThemeApi.getActiveThemeId() === 'draeger-dark',
  );
  useEffect(() => {
    const sub = appThemeApi
      .activeThemeId$()
      .subscribe(id => setIsDark(id === 'draeger-dark'));
    return () => sub.unsubscribe();
  }, [appThemeApi]);

  const toggleTheme = useCallback(() => {
    appThemeApi.setActiveThemeId(isDark ? 'draeger-light' : 'draeger-dark');
  }, [appThemeApi, isDark]);

  // The context handed to every command's run/visible — keeps commands pure.
  const commandCtx: CommandContext = {
    navigate,
    close: onClose,
    toggleTheme,
    isDark,
    isAdmin,
    pushRecent,
  };
  const commands = buildCommands(commandCtx);

  // Resolve a command's displayed title. The theme-toggle title flips with the
  // current theme so the label always states the action it will perform.
  const resolveTitle = useCallback(
    (cmd: Command): string => {
      if (cmd.id === 'action.toggle-theme') {
        return tk(isDark ? 'palette.action.switchToLight' : 'palette.action.switchToDark');
      }
      return tk(cmd.titleKey);
    },
    [tk, isDark],
  );

  // Tracks whether the last settled search had results: null = not yet known / no term.
  // Updated from inside the SearchResultState render prop; reset on term change.
  const [hasResults, setHasResults] = useState<boolean | null>(null);
  useEffect(() => {
    // When the term changes, reset so the suggestion hook doesn't fire on stale state.
    setHasResults(null);
  }, [term]);

  const suggestions = useZeroResultSuggestions(term, hasResults);

  // A settled zero-result may just mean the index is still building (cold start) — check
  // /api/search-status so we can show "warming up" instead of a false "no results".
  const checkWarming = useSearchStatus();
  const [warming, setWarming] = useState(false);
  useEffect(() => {
    if (hasResults !== false) {
      setWarming(false);
      return undefined;
    }
    let cancelled = false;
    checkWarming().then(w => {
      if (!cancelled) setWarming(w);
    });
    return () => {
      cancelled = true;
    };
  }, [hasResults, term, checkWarming]);

  // #92/#103: "search within the current document". When opened on a doc reader
  // page we default to a doc-scoped search; the user can broaden to everything.
  const [scoped, setScoped] = useState(Boolean(currentDoc));

  // Kind filter — only active in unscoped (non-doc) mode. Resets when scoped changes.
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  useEffect(() => {
    // When the user enters scoped mode (doc context) or a new term arrives, reset kind filter.
    setKindFilter('all');
  }, [scoped]);

  // Apply the scope to the shared search context whenever it changes. Scoped =
  // techdocs-only, filtered to this entity; unscoped = all types, no filters.
  // When unscoped, the kindFilter further constrains types.
  useEffect(() => {
    if (scoped && currentDoc) {
      setTypes(['techdocs']);
      setFilters(docFilters(currentDoc));
    } else if (kindFilter === 'docs') {
      setTypes(['techdocs']);
      setFilters({});
    } else if (kindFilter === 'catalog') {
      setTypes(['software-catalog']);
      setFilters({});
    } else {
      setTypes([]);
      setFilters({});
    }
  }, [scoped, currentDoc, kindFilter, setTypes, setFilters]);

  // ── Arrow-key navigation of results + commands ─────────────────────────────
  // Owns the highlighted-row index and the is-active DOM sync; the panel root ref
  // is attached below so the nav sees command rows AND result rows in DOM order.
  const { panelRef, handleKeyNav } = useResultKeyboardNav({ term, kindFilter, scoped });

  // Focus the search input on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      const input = document.querySelector(
        '.devhub-search-modal__bar input',
      ) as HTMLInputElement;
      input?.focus();
    }, FOCUS_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  // Close on ESC
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Navigate to a result's location — internal route via the router, external URL via the browser.
  const goToResult = useCallback(
    (location: string) => {
      if (/^https?:\/\//.test(location)) window.location.href = location;
      else navigate(location);
    },
    [navigate],
  );

  // Close shortly after a result is chosen (lets the navigation fire first).
  const handleResultClick = useCallback(() => {
    setTimeout(() => onClose(), CLOSE_DELAY_MS);
  }, [onClose]);

  // Matched commands for the active term (empty term → none; shown as groups).
  const matchedCommands = matchCommands(term, commands, resolveTitle);
  // Split the full set into groups for the empty-term view.
  const navCommands = commands.filter(c => c.kind === 'route');
  const actionCommands = commands.filter(c => c.kind === 'action');

  // A single command row — a native ODX-styled button (NOT MUI). Included in the
  // arrow-nav selector via the shared .devhub-cmdk__item class.
  const renderCommandButton = (cmd: Command) => (
    <button
      key={cmd.id}
      type="button"
      className="devhub-cmdk__item"
      onClick={() => cmd.run(commandCtx)}
    >
      <odx-icon className="odx-icon" name={cmd.icon} size="sm" />
      <span className="devhub-cmdk__item-title">{resolveTitle(cmd)}</span>
    </button>
  );

  const placeholder =
    scoped && currentDoc
      ? t('modal.searchInDoc', { doc: currentDoc.name })
      : t('placeholder');

  return (
    // Backdrop click-to-close is a redundant mouse affordance; keyboard users
    // close via Esc (handled by the modal) and the explicit ESC button below.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="devhub-search-modal__backdrop" onClick={onClose}>
      {/* Panel only stops backdrop clicks from bubbling — not an interactive control. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        ref={panelRef}
        className="devhub-search-modal__panel"
        onClick={e => e.stopPropagation()}
        onKeyDown={handleKeyNav}
      >
        {/* Search input — clicking anywhere in the padded bar (icon, gap, whitespace)
            focuses the input, not just the input box itself. This is a MOUSE-only
            convenience over an already-focusable <input>; keyboard users tab straight
            to the input, so no keyboard handler / interactive role is needed here. */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div
          className="devhub-search-modal__bar"
          onClick={() => inputRef.current?.focus()}
        >
          <odx-icon name="core::search" size="md" />
          <input
            ref={inputRef}
            className="devhub-search-modal__input"
            type="text"
            placeholder={placeholder}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            aria-label={placeholder}
            {...noBrowserAutofill}
          />
        </div>

        {/* Kind filter chips — only in unscoped (non-doc) mode. Lets the user
            narrow the search to Docs or Catalog results. Hidden when a currentDoc
            scope-toggle is active, since that has its own scope control. */}
        {!currentDoc && (
          <div className="devhub-search-modal__kind-filters" role="group" aria-label="Filter results by type">
            {(['all', 'docs', 'catalog'] as KindFilter[]).map(filter => (
              <button
                key={filter}
                type="button"
                className={`devhub-search-modal__kind-chip ${kindFilter === filter ? 'is-active' : ''}`}
                aria-pressed={kindFilter === filter}
                onClick={() => setKindFilter(filter)}
              >
                {filter === 'all' && t('modal.filterAll')}
                {filter === 'docs' && t('modal.filterDocs')}
                {filter === 'catalog' && t('modal.filterCatalog')}
              </button>
            ))}
          </div>
        )}

        {/* #92: In-doc search scope selector — only when reading a doc. Lets the
            user narrow to just the current document or search everything. */}
        {currentDoc && (
          <div className="devhub-search-modal__scope">
            <span className="devhub-search-modal__scope-label">
              <odx-icon name="core::document" size="sm" />
              {t('modal.search')}
            </span>
            <div className="devhub-search-modal__scope-toggle">
              <button
                type="button"
                className={`devhub-search-modal__scope-btn ${scoped ? 'is-active' : ''}`}
                aria-pressed={scoped}
                onClick={() => setScoped(true)}
                title={t('modal.searchOnlyWithin', { doc: currentDoc.name })}
              >
                {t('modal.inThisDoc')}
              </button>
              <button
                type="button"
                className={`devhub-search-modal__scope-btn ${!scoped ? 'is-active' : ''}`}
                aria-pressed={!scoped}
                onClick={() => setScoped(false)}
              >
                {t('modal.everywhere')}
              </button>
            </div>
          </div>
        )}

        {/* ── Command palette layer (renders ABOVE live search results) ──
            Empty term → Recent, Navigate, Actions. Non-empty term → the matched
            commands under a "Go to / Do" group above the live results. Rendered
            as native ODX buttons; the arrow-nav selector spans these + results. */}
        {!term && (
          <div className="devhub-cmdk">
            {recent.length > 0 && (
              <div className="devhub-cmdk__group">
                <div className="devhub-cmdk__group-label">{tk('palette.group.recent')}</div>
                {recent.map(item => (
                  <button
                    key={`recent-${item.id}`}
                    type="button"
                    className="devhub-cmdk__item"
                    onClick={() => {
                      // Re-record (moves to front) then navigate.
                      pushRecent(item);
                      navigate(item.path);
                      onClose();
                    }}
                  >
                    <odx-icon className="odx-icon" name={item.icon} size="sm" />
                    <span className="devhub-cmdk__item-title">
                      {/* Recents store an i18n key when the source was a route
                          command; fall back to the raw title for anything else. */}
                      {item.title.startsWith('palette.') ? tk(item.title) : item.title}
                    </span>
                  </button>
                ))}
              </div>
            )}
            <div className="devhub-cmdk__group">
              <div className="devhub-cmdk__group-label">{tk('palette.group.navigate')}</div>
              {navCommands.map(renderCommandButton)}
            </div>
            <div className="devhub-cmdk__group">
              <div className="devhub-cmdk__group-label">{tk('palette.group.actions')}</div>
              {actionCommands.map(renderCommandButton)}
            </div>
          </div>
        )}
        {term && matchedCommands.length > 0 && (
          <div className="devhub-cmdk">
            <div className="devhub-cmdk__group">
              <div className="devhub-cmdk__group-label">{tk('palette.group.matches')}</div>
              {matchedCommands.map(renderCommandButton)}
            </div>
          </div>
        )}

        {/* Results */}
        {term && (
          <SearchModalResults
            term={term}
            warming={warming}
            scoped={scoped}
            currentDoc={currentDoc}
            suggestions={suggestions}
            setHasResults={setHasResults}
            setTerm={setTerm}
            setScoped={setScoped}
            goToResult={goToResult}
            handleResultClick={handleResultClick}
            onClose={onClose}
          />
        )}

        {/* Empty state — a slim "start typing" hint under the command groups.
            The old browse shortcuts (Catalog / Docs / APIs) are superseded by the
            palette's Navigate group above, so only the prompt line remains. */}
        {!term && (
          <div className="devhub-search-modal__empty devhub-search-modal__empty--hint">
            <p>
              {scoped && currentDoc
                ? t('modal.startTypingDoc', { doc: currentDoc.name })
                : t('modal.startTypingAll')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function SearchModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  // Detect the current doc BEFORE the provider mounts so we can seed the scoped
  // initial state — this keeps the very first query doc-scoped when applicable.
  const currentDoc = useCurrentDoc();

  if (!open) return null;

  const initialState = currentDoc
    ? {
        term: '',
        types: ['techdocs'],
        filters: docFilters(currentDoc),
        pageLimit: 25,
      }
    : { term: '', types: [], filters: {}, pageLimit: 25 };

  return createPortal(
    <SearchContextProvider initialState={initialState}>
      <SearchModalContent onClose={onClose} currentDoc={currentDoc} />
    </SearchContextProvider>,
    document.body,
  );
}

// Hook for managing search modal state
export function useSearchModal() {
  const [open, setOpen] = useState(false);

  const openSearch = useCallback(() => setOpen(true), []);
  const closeSearch = useCallback(() => setOpen(false), []);

  // Note: the global Cmd+K / Ctrl+K (and "/") shortcut that calls openSearch is
  // registered once in Root.tsx, which wraps all authenticated pages.

  return { open, openSearch, closeSearch };
}
